import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, ListGroup } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import CaseLevelHeader from 'components/features/dashboard/CaseLevelHeader'
import PhaseStatus from 'components/molecules/PhaseStatus'
import { Orientation } from 'share/domain/enums'
import CustomModal from 'components/organisms/CustomModal'
import { routeToQuestionnairePage } from 'share/utils/questionnaireUtils'
import { AccountCreationStep } from 'share/utils/utils'
import { logOut } from 'share/utils/authenticationUtils'

import {
  CaseLevelDashBtnList,
  CaseLevelDashDescription,
  CaseLevelDashPage,
  CaseLevelDashToDoContainer,
  CaseLevelDashToKnowContainer,
  HeroImageContainer,
  List
} from './styles'
import { setTabSubmit } from 'reducers/document/documentProcessingSlice'

const imageBasePath = '/assets/images/'

const CaseOverview: FC = () => {
  const history = useHistory()
  const authentication = useAppSelector((state) => state.authentication)
  const activeCase = useAppSelector((state) => state.activeCase)
  const dispatch = useAppDispatch()
  const [showModal, setShowModal] = useState(false)
  const questionnaireShownInstructionsFlag = useAppSelector(
    (state) => state.activeCase.matter?.questionnaireShownInstructionsFlag
  )
  const questionnaireShownTermsAndConditionsFlag = useAppSelector(
    (state) => state.activeCase.matter?.questionnaireShownTermsAndConditionsFlag
  )
  const [imagePath, setImagePath] = useState<string>('')
  const [imageClass, setImageClass] = useState<string>('hero-image')

  useEffect(() => {
    if (authentication) {
      if (authentication.account) {
        if (
          authentication.account.accountCreationStep !=
          AccountCreationStep.CompletedAllSteps
        ) {
          logOut(dispatch)
        }
      }
    }
  }, [authentication])

  useEffect(() => {
    switch (activeCase.matter?.status) {
      case 'Pre-Filing':
        setImagePath(imageBasePath + 'case-pre-filing.jpg')
        setImageClass('case-pre-filing hero-image')
        break
      case 'Filed':
        setImagePath(imageBasePath + 'case-filed.jpg')
        setImageClass('case-filed hero-image')
        break
      case 'Complete':
        setImagePath(imageBasePath + 'case-complete.jpg')
        setImageClass('case-complete hero-image')
        break
    }
  }, [activeCase.matter?.status])

  function onClickQuestionnaire() {
    const questionnaireRoute = routeToQuestionnairePage(
      questionnaireShownInstructionsFlag || false,
      questionnaireShownTermsAndConditionsFlag || false
    )
    history.push(questionnaireRoute)
  }

  function onClickPhaseStatus() {
    setShowModal(true)
  }

  return (
    <>
      <CaseLevelHeader
        text={activeCase.matter?.firm.name}
        status={
          activeCase.matter?.dashBoardHeaderDescription
            ? activeCase.matter?.dashBoardHeaderDescription
            : ''
        }
        iconUrl={activeCase.matter?.firm.iconUrl}
      />
      <HeroImageContainer>
        {activeCase.matter?.status && imagePath ? (
          <div
            className={`${imageClass}`}
            style={{
              backgroundImage: `url(${imagePath})`
            }}
          >
            {}
          </div>
        ) : (
          <></>
        )}
      </HeroImageContainer>

      <CaseLevelDashPage>
        <h2 data-testid='caselevel-dashboard-title'>
          {activeCase.matter?.dashBoardTitle}
        </h2>
        <PhaseStatus
          orientation={Orientation.horizontal}
          statusList={activeCase.matter?.statuses}
          onClick={onClickPhaseStatus}
        />
        <CaseLevelDashToKnowContainer>
          <div>
            <h3 data-testid='caselevel-dashboard-needtoknow'>
              What you need to know
            </h3>
          </div>
          {activeCase.matter?.dashBoardCurrentStatusDescription && (
            <CaseLevelDashDescription data-testid='caselevel-dashboard-status-description'>
              {activeCase.matter?.dashBoardCurrentStatusDescription}
            </CaseLevelDashDescription>
          )}
        </CaseLevelDashToKnowContainer>
        <CaseLevelDashToDoContainer>
          <h4 data-testid='caselevel-dashboard-needtodo'>
            What you need to do
          </h4>
          <List>
            {activeCase.matter?.dashBoardCourseNumber && (
              <ListGroup.Item
                action
                data-testid={`caselevel-dashboard-take-${activeCase.matter?.dashBoardCourseNumber}-bankruptcy-course-button`}
                onClick={() => history.push('/dashboard/courses')}
              >
                Take {activeCase.matter?.dashBoardCourseNumber} Bankruptcy
                Course
                <span className='mdi mdi-chevron-right float-right'></span>
              </ListGroup.Item>
            )}
            {activeCase.matter?.dashBoardShowSubmitDocumentsFlag && (
              <ListGroup.Item
                action
                data-testid='caselevel-dashboard-submit-documents-button'
                onClick={() => {
                  dispatch(setTabSubmit())
                  history.push('/dashboard/documents')
                }}
              >
                Submit Requested Documents
                <span className='mdi mdi-chevron-right float-right'></span>
              </ListGroup.Item>
            )}
            {activeCase.matter?.questionnaireDisplayInClientPortalFlag && (
              <ListGroup.Item
                action
                data-testid='caselevel-dashboard-questionnaire-button'
                onClick={() => onClickQuestionnaire()}
              >
                Complete Intake Questionnaire
                <span className='mdi mdi-chevron-right float-right'></span>
              </ListGroup.Item>
            )}
          </List>
          {!activeCase.matter?.dashBoardCourseNumber &&
            !activeCase.matter?.dashBoardShowSubmitDocumentsFlag && (
              <div
                data-testid='caselevel-dashboard-completed-all-tasks-message'
                className='case-level-dashboard-completed-all-tasks-message'
              >
                <div className='case-level-dashboard-completed-all-task-message-icon'>
                  <span className='mdi mdi-checkbox-multiple-marked-circle-outline' />
                </div>
                <span className='case-level-dashboard-completed-all-task-message-text'>
                  You have nothing left to do.
                </span>
              </div>
            )}
        </CaseLevelDashToDoContainer>
        <CaseLevelDashBtnList>
          <Button
            variant='outline-secondary'
            data-testid='caselevel-dashboard-faqs-button'
            onClick={() => {
              history.push('/dashboard/frequentlyaskedquestions')
            }}
          >
            FAQs
          </Button>
          <Button
            variant='outline-secondary'
            data-testid='caselevel-dashboard-contact-attorney-button'
            onClick={() => {
              history.push('/dashboard/lawfirminformation')
            }}
          >
            Contact Your Attorney
          </Button>
        </CaseLevelDashBtnList>
      </CaseLevelDashPage>
      <CustomModal
        showClose
        show={showModal}
        showFooter={false}
        title='Looking Ahead'
        onHide={() => setShowModal(false)}
      >
        <PhaseStatus
          orientation={Orientation.vertical}
          statusList={activeCase.matter?.statuses}
          onClick={onClickPhaseStatus}
        />
      </CustomModal>
    </>
  )
}

export default CaseOverview
